import { FormEvent, useMemo, useState } from "react";
import {
  AlertTriangle,
  BatteryMedium,
  CheckCircle2,
  CloudRain,
  Compass,
  LocateFixed,
  MapPin,
  Mountain,
  Navigation,
  Route,
  Send,
  ShieldCheck,
  Snowflake,
  Sparkles,
  WifiOff,
} from "lucide-react";
import { MountainAIIntegrationService } from "./integration/MountainAIIntegrationService";
import type { TheraRuntimeSnapshot } from "./integration/TheraIntegrationInput";
import type { AssistantResponse, MountainSeverity } from "./engine/types/AssistantResponse";

interface Props {
  snapshot: TheraRuntimeSnapshot;
}

const routeCommands = [
  ["¿Cuánto falta?", "¿Cuánto falta?"],
  ["Progreso", "¿Qué porcentaje llevo?"],
  ["Fuera de ruta", "¿Estoy fuera de ruta?"],
  ["Altitud", "¿A qué altura estoy?"],
  ["Estado GPS", "¿Cómo está el GPS?"],
  ["Estado de ruta", "¿Cuál es mi estado de ruta?"],
] as const;

const adviceCommands = [
  ["Tengo frío", "Tengo frío, ¿qué hago?", Snowflake],
  ["Ropa mojada", "Mi ropa está mojada", CloudRain],
  ["Está oscureciendo", "Está oscureciendo", Mountain],
  ["Ahorrar batería", "¿Cómo ahorro batería?", BatteryMedium],
  ["Perdí la ruta", "Creo que estoy perdido", Compass],
  ["Hay neblina", "Hay neblina", CloudRain],
  ["Escucho truenos", "Escucho truenos", AlertTriangle],
  ["Revisar equipo", "¿Qué equipo básico debo revisar?", ShieldCheck],
] as const;

const severityLabel: Record<MountainSeverity, string> = {
  INFORMATION: "Información",
  CAUTION: "Precaución",
  WARNING: "Advertencia",
  CRITICAL: "Crítico",
  INSUFFICIENT_DATA: "Datos insuficientes",
  OFFLINE_LIMITED: "Offline limitado",
};

export function TheraMountainAIPage({ snapshot }: Props) {
  const service = useMemo(() => new MountainAIIntegrationService(), []);
  const [input, setInput] = useState("");
  const [response, setResponse] = useState<AssistantResponse | null>(null);
  const [message, setMessage] = useState("Elige un comando o escribe una pregunta compatible con Mountain AI.");
  const [busy, setBusy] = useState(false);

  const routeAvailable = Boolean(snapshot.route);
  const gpsAvailable = Boolean(snapshot.gps);
  const gpsAccuracy = snapshot.gps?.accuracyMeters;
  const altitude = snapshot.gps?.altitudeMeters;
  const weather = snapshot.weather;
  const weatherAvailable = Boolean(weather);
  const weatherSummary = weather
    ? [
        weather.condition,
        weather.temperatureCelsius !== null && weather.temperatureCelsius !== undefined ? `${Math.round(weather.temperatureCelsius)} C` : null,
        weather.windSpeedKph !== null && weather.windSpeedKph !== undefined ? `viento ${Math.round(weather.windSpeedKph)} km/h` : null,
      ].filter((value): value is string => Boolean(value)).join(" · ")
    : "Sin clima disponible";

  const ask = async (question: string) => {
    const clean = question.trim();
    if (!clean || busy) return;
    setBusy(true);
    try {
      const result = await service.ask(clean, snapshot);
      if (!result.recognized || !result.response) {
        setResponse(null);
        setMessage("Ese comando no está disponible en esta Beta. Usa una de las preguntas documentadas; Mountain AI no inventará una respuesta.");
        return;
      }
      setResponse(result.response);
      setMessage("");
      setInput("");
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void ask(input);
  };

  return (
    <section className="thera-product-page thera-ai-page" aria-labelledby="thera-mountain-ai-title">
      <header className="thera-product-hero thera-ai-hero">
        <span className="thera-product-icon"><Sparkles /></span>
        <div>
          <p className="eyebrow">THERA Mountain AI · BETA V1.2</p>
          <h1 id="thera-mountain-ai-title">Asistente de montaña con datos de THERA.</h1>
          <p>40 comandos deterministas. Lee el contexto disponible, funciona de forma limitada offline y no modifica GPS, mapa ni navegación.</p>
        </div>
        <span className="thera-status beta">Beta V1.2</span>
      </header>

      <div className="mountain-ai-live-status" aria-label="Datos disponibles para Mountain AI">
        <article className={gpsAvailable ? "available" : "unavailable"}>
          <LocateFixed />
          <span><strong>GPS</strong><small>{gpsAvailable ? `Precisión ±${Math.round(gpsAccuracy ?? 0)} m` : "Sin posición disponible"}</small></span>
        </article>
        <article className={routeAvailable ? "available" : "unavailable"}>
          <Route />
          <span><strong>Ruta</strong><small>{routeAvailable ? `${Math.round(snapshot.route?.progressPercent ?? 0)} % · ${((snapshot.route?.distanceRemainingMeters ?? 0) / 1000).toFixed(1)} km restantes` : "Sin ruta activa"}</small></span>
        </article>
        <article className={snapshot.networkState === "ONLINE" ? "available" : "limited"}>
          {snapshot.networkState === "ONLINE" ? <Navigation /> : <WifiOff />}
          <span><strong>{snapshot.networkState === "ONLINE" ? "En línea" : "Sin conexión"}</strong><small>{snapshot.networkState === "ONLINE" ? "Contexto local disponible" : "Comandos locales limitados"}</small></span>
        </article>
        <article className={altitude !== null && altitude !== undefined ? "available" : "unavailable"}>
          <Mountain />
          <span><strong>Altitud</strong><small>{altitude !== null && altitude !== undefined ? `${Math.round(altitude)} m s. n. m.` : "Sin dato de altitud"}</small></span>
        </article>
        <article className={weatherAvailable ? (weather?.cached ? "limited" : "available") : "unavailable"}>
          <CloudRain />
          <span><strong>Clima</strong><small>{weatherSummary}{weather?.cached ? " · cache local" : ""}</small></span>
        </article>
      </div>

      <section className="mountain-ai-console" aria-label="Comandos de Mountain AI">
        <div className="mountain-ai-command-panel">
          <div className="mountain-ai-section-heading">
            <div><p className="eyebrow">RUTA Y GPS</p><h2>Preguntas rápidas</h2></div>
            <span>15 comandos de datos</span>
          </div>
          <div className="mountain-ai-quick-grid">
            {routeCommands.map(([label, question]) => (
              <button type="button" key={question} onClick={() => void ask(question)} disabled={busy}>
                <MapPin /><span>{label}</span>
              </button>
            ))}
          </div>

          <details className="mountain-ai-advice-catalog">
            <summary><Snowflake /> Consejos simples de montaña <span>25 comandos</span></summary>
            <div className="mountain-ai-advice-grid">
              {adviceCommands.map(([label, question, Icon]) => (
                <button type="button" key={question} onClick={() => void ask(question)} disabled={busy}>
                  <Icon /><span>{label}</span>
                </button>
              ))}
            </div>
            <p>También reconoce consejos de capas de ropa, viento, lluvia, pausas, agua, comida, sol, regreso, altitud, terreno resbaloso, orientación, ayuda y preparación antes de salir.</p>
          </details>

          <form className="mountain-ai-input" onSubmit={submit}>
            <label htmlFor="mountain-ai-question">Escribe un comando</label>
            <div>
              <input
                id="mountain-ai-question"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ej. ¿Cuánto falta? / Tengo frío, ¿qué hago?"
                autoComplete="off"
              />
              <button type="submit" disabled={!input.trim() || busy} aria-label="Enviar comando a Mountain AI"><Send /></button>
            </div>
          </form>
        </div>

        <aside className="mountain-ai-response-panel" aria-live="polite">
          <div className="mountain-ai-section-heading">
            <div><p className="eyebrow">RESPUESTA CONTROLADA</p><h2>Mountain AI</h2></div>
            <ShieldCheck />
          </div>
          {response ? (
            <div className={`mountain-ai-response severity-${response.severity.toLowerCase()}`}>
              <span className="mountain-ai-severity">{severityLabel[response.severity]}</span>
              <strong>{response.message}</strong>
              <div className="mountain-ai-action"><CheckCircle2 /><p><b>Acción recomendada</b>{response.recommended_action}</p></div>
              {response.limitations.length > 0 && (
                <div className="mountain-ai-limitations">
                  <b>Límites</b>
                  <ul>{response.limitations.map((limit) => <li key={limit}>{limit}</li>)}</ul>
                </div>
              )}
              <small>Fuente: {response.source} · Confianza del dato: {Math.round(response.confidence * 100)} %</small>
            </div>
          ) : (
            <div className="mountain-ai-empty-response">
              <Sparkles />
              <p>{message}</p>
            </div>
          )}
        </aside>
      </section>

      <div className="thera-control-note">
        <ShieldCheck />
        <div><strong>Límite del asistente</strong><p>Mountain AI informa y recomienda de forma general. No diagnostica, no garantiza seguridad, no sustituye al guía ni confirma rescates o comunicaciones de emergencia.</p></div>
      </div>
      <div className="thera-demo-box mountain-ai-safety-note">
        <AlertTriangle />
        <div><strong>Beta de campo</strong><p>Los umbrales de precisión GPS y desviación siguen siendo experimentales hasta completar validación de campo. Las alertas críticas deterministas tienen prioridad sobre cualquier recomendación.</p></div>
      </div>
    </section>
  );
}
